import { useState, useEffect } from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button, 
  TextField, 
  Box, 
  Typography, 
  IconButton,
  CircularProgress,
  useMediaQuery
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';

interface HomePreferencesDialogProps {
  open: boolean;
  onClose: () => void;
  currentPreferences: string;
  onSave: (preferences: string) => Promise<void>;
  title?: string;
  description?: string;
}

const HomePreferencesDialog = ({ 
  open, 
  onClose, 
  currentPreferences, 
  onSave,
  title = "Your Home Preferences",
  description = "Describe what you're looking for in a home. This information will help tailor your experience."
}: HomePreferencesDialogProps) => {
  const [editingPreferences, setEditingPreferences] = useState(currentPreferences);
  const [isSaving, setIsSaving] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setEditingPreferences(currentPreferences);
    }
  }, [open, currentPreferences]);

  const handleSavePreferences = async () => {
    setIsSaving(true);
    try {
      await onSave(editingPreferences);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => !isSaving && onClose()}
      fullScreen={isMobile}
      maxWidth="md"
      sx={{
        '& .MuiDialog-paper': {
          height: { sm: '80vh' },
          maxHeight: { sm: '80vh' },
          borderRadius: isMobile ? 0 : undefined 
        },
        zIndex: 3001,
      }}
    >
      <DialogTitle 
        sx={{ 
          display: 'flex', 
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 1
        }}
      >
        <Typography component="span" variant="h6">{title}</Typography>
        <IconButton 
          edge="end" 
          onClick={() => !isSaving && onClose()} 
          aria-label="close"
          size="small"
          disabled={isSaving}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <Box sx={{ px: 3, pb: 2 }}>
        <Typography variant="body1" color="text.secondary">
          {description}
        </Typography>
      </Box>
      
      <DialogContent 
        dividers
        sx={{
          display: 'flex',
          flexDirection: 'column',
          p: 3,
          height: '100%',
          minHeight: '300px'
        }}
      >
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          height: '100%',
          flex: 1 
        }}>
          <TextField
            autoFocus
            multiline
            fullWidth
            value={editingPreferences}
            onChange={(e) => setEditingPreferences(e.target.value)}
            placeholder="Example: I'm looking for a 3-bedroom house with at least 2 bathrooms, preferably in a quiet neighborhood close to good schools. I'd like a modern kitchen, hardwood floors, and a backyard..."
            variant="outlined"
            disabled={isSaving}
            sx={{ 
              height: '100%',
              display: 'flex',
              '& .MuiInputBase-root': {
                height: '100%',
                display: 'flex',
                flexDirection: 'column'
              },
              '& .MuiInputBase-input': {
                height: '100% !important',
                flex: 1
              },
              '& .MuiOutlinedInput-root': {
                height: '100%',
                display: 'flex',
                flexDirection: 'column'
              }
            }}
            InputProps={{
              sx: {
                height: '100%',
                alignItems: 'flex-start'
              }
            }}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button 
          onClick={onClose}
          disabled={isSaving}
        >
          Cancel
        </Button>
        <Button 
          onClick={handleSavePreferences} 
          variant="contained"
          disabled={isSaving}
          startIcon={isSaving ? <CircularProgress size={20} color="inherit" /> : null}
        >
          {isSaving ? 'Saving...' : 'Save Preferences'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default HomePreferencesDialog;